import * as React from 'react'

import { Plus, Trash2 } from 'lucide-react'
import { MobXProviderContext } from 'mobx-react'
import { FormProvider, useFormContext } from 'react-hook-form'

import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import Localization from '../../localization/LocalizationManager'
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog'
import { DynamicFieldRenderer, useDynamicForm } from '../DynamicForm'
import { DYNAMIC_FORM_ID } from '../DynamicForm/types'
import { Loader } from '../Loader'
import { PanelSections } from '../PanelSections/PanelSections'
import { Button } from '../shadcn-ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../shadcn-ui/dropdown-menu'
import { SidePanel } from '../SidePanel/SidePanel'
import StruxhubSearchableSelect, {
  SelectOption,
} from '../StruxhubInputs/StruxhubSearchableSelect/StruxhubSearchableSelect'
import { buildBulkEditChanges } from './resourceBulkEditChanges'
import {
  ResourceBulkEditContext,
  ResourceBulkEditContextValue,
  useResourceBulkEdit,
} from './ResourceBulkEditContext'
import { computeAddableFieldOptions } from './resourceEditFieldVisibility'
import { useResourceFields } from './useResourceFields'
import { useResourceFileUpload } from './useResourceFileUpload'

interface IRootProps {
  resourceTypeId: string
  selectedCount: number
  totalCount: number
  onClose: () => void
  onSave: (formData: Record<string, any>) => Promise<void>
  resourceSchema: ResourceSchema
  children: React.ReactNode
}

interface IMobXContext {
  projectDateStore?: ProjectDateStore
}

const Root: React.FC<IRootProps> = ({
  resourceTypeId,
  selectedCount,
  totalCount,
  onClose,
  onSave,
  resourceSchema,
  children,
}) => {
  const { projectDateStore } = React.useContext(
    MobXProviderContext,
  ) as IMobXContext

  const propertyDefinitions = resourceSchema.properties

  const { fields, updateField } = useResourceFields({
    record: null,
    propertyDefinitions,
    recordId: null,
    resourceTypeId,
    projectDateStore,
    fieldOptions: {
      includeStaticFields: false,
      allOptional: true,
      includeAllProperties: true,
    },
  })

  const editableFields = React.useMemo(
    () => fields.filter(field => !field.readOnly),
    [fields],
  )

  const [selectedFieldNames, setSelectedFieldNames] = React.useState<string[]>(
    [],
  )
  const [isSaving, setIsSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false)
  const pendingFormDataRef = React.useRef<Record<string, unknown> | null>(null)

  const formFields = React.useMemo(
    () =>
      selectedFieldNames
        .map(name => editableFields.find(f => f.name === name))
        .filter((field): field is (typeof editableFields)[number] =>
          Boolean(field),
        ),
    [editableFields, selectedFieldNames],
  )

  const formMethods = useDynamicForm(formFields)

  React.useEffect(() => {
    setSelectedFieldNames(prev => {
      const next = prev.filter(name =>
        editableFields.some(f => f.name === name),
      )
      return next.length === prev.length ? prev : next
    })
  }, [editableFields])

  const requestSave = React.useCallback(
    (formData: Record<string, unknown>) => {
      setSaveError(null)

      if (formFields.length === 0) {
        setSaveError(Localization.translator.selectFields)
        return
      }

      pendingFormDataRef.current = buildBulkEditChanges(formFields, formData)
      setIsConfirmOpen(true)
    },
    [formFields],
  )

  const confirmSave = React.useCallback(async () => {
    const formData = pendingFormDataRef.current
    if (!formData) return

    setIsConfirmOpen(false)
    setIsSaving(true)
    setSaveError(null)

    try {
      await onSave(formData)
      pendingFormDataRef.current = null
      onClose()
    } catch (error) {
      console.error('Failed to bulk edit:', error)
      setSaveError(Localization.translator.failedToSaveChangesTryAgain)
    } finally {
      setIsSaving(false)
    }
  }, [onSave, onClose])

  const cancelConfirm = React.useCallback(() => {
    setIsConfirmOpen(false)
    pendingFormDataRef.current = null
  }, [])

  const addField = React.useCallback((fieldName: string) => {
    if (!fieldName) return
    setSelectedFieldNames(prev =>
      prev.includes(fieldName) ? prev : [...prev, fieldName],
    )
    setSaveError(null)
  }, [])

  const removeField = React.useCallback((fieldName: string) => {
    setSelectedFieldNames(prev => prev.filter(name => name !== fieldName))
  }, [])

  const loading = resourceSchema.loading

  const pendingFieldCount = formFields.length

  const contextValue = React.useMemo(
    (): ResourceBulkEditContextValue => ({
      state: {
        selectedFieldNames,
        editableFields,
        formFields,
        isSaving,
        saveError,
        selectedCount,
        totalCount,
        loading,
        isConfirmOpen,
        pendingFieldCount,
      },
      actions: {
        addField,
        removeField,
        save: requestSave,
        confirmSave,
        cancelConfirm,
        close: onClose,
        updateField,
        clearError: () => setSaveError(null),
      },
    }),
    [
      selectedFieldNames,
      editableFields,
      formFields,
      isSaving,
      saveError,
      selectedCount,
      totalCount,
      loading,
      isConfirmOpen,
      pendingFieldCount,
      addField,
      removeField,
      requestSave,
      confirmSave,
      cancelConfirm,
      onClose,
      updateField,
    ],
  )

  if (loading) {
    return (
      <SidePanel>
        <Loader />
      </SidePanel>
    )
  }

  return (
    <ResourceBulkEditContext.Provider value={contextValue}>
      <FormProvider {...formMethods}>
        <SidePanel testId="resource-bulk-edit-panel">{children}</SidePanel>
      </FormProvider>
    </ResourceBulkEditContext.Provider>
  )
}

const Header: React.FC = () => {
  const {
    actions: { close },
  } = useResourceBulkEdit()

  return (
    <PanelSections.Header
      title={Localization.translator.bulkEdit}
      onClose={close}
    />
  )
}

interface IContentProps {
  children: React.ReactNode
}

const Content: React.FC<IContentProps> = ({ children }) => {
  return <div className="tw:flex-1 tw:overflow-y-auto">{children}</div>
}

const FieldSelector: React.FC = () => {
  const {
    state: { selectedFieldNames, editableFields },
    actions: { addField },
  } = useResourceBulkEdit()

  const fieldOptions: SelectOption[] = React.useMemo(
    () =>
      computeAddableFieldOptions(editableFields, new Set(selectedFieldNames)),
    [editableFields, selectedFieldNames],
  )

  const handleFieldChange = React.useCallback(
    (value: string | number | null | (string | number | null)[]) => {
      const single = Array.isArray(value) ? value[0] : value
      addField(String(single ?? ''))
    },
    [addField],
  )

  // Before any property is picked, always show the selector — even with no
  // options it renders an empty "no results" dropdown (e.g. sensors, which have
  // no editable properties), rather than blanking the panel.
  if (selectedFieldNames.length === 0) {
    return (
      <div className="tw:px-4" data-testid="resource-bulk-edit-field-selector">
        <StruxhubSearchableSelect
          label={Localization.translator.selectProperty}
          options={fieldOptions}
          value={null}
          onChange={handleFieldChange}
          placeholder={Localization.translator.selectProperty}
          isRequiredTextHidden={true}
          isHelperTextHidden={true}
          allowUserCreation={false}
        />
      </div>
    )
  }

  // Once properties are selected, the "add another" control only appears when
  // there are still unselected properties left to add.
  if (fieldOptions.length === 0) return null

  return (
    <div className="tw:flex tw:justify-end tw:px-4 tw:pt-3">
      <DropdownMenu>
        <DropdownMenuTrigger className="tw:inline-flex tw:items-center tw:gap-1 tw:rounded-md tw:border-0 tw:bg-transparent tw:px-2 tw:py-1 tw:text-body-sm tw:text-primary tw:cursor-pointer tw:outline-none tw:hover:bg-muted tw:focus-visible:ring-2 tw:focus-visible:ring-ring">
          <Plus className="tw:size-4" />
          {Localization.translator.addProperty}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="tw:w-64 tw:max-h-72">
          {fieldOptions.map(option => (
            <DropdownMenuItem
              key={String(option.value)}
              onClick={() => addField(String(option.value))}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

const Form: React.FC = () => {
  const fileUploadFn = useResourceFileUpload()
  const {
    state: { formFields },
    actions: { save, updateField, removeField },
  } = useResourceBulkEdit()

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<Record<string, unknown>>()

  if (formFields.length === 0) return null

  return (
    <form
      id={DYNAMIC_FORM_ID}
      onSubmit={handleSubmit(save)}
      className="tw:flex tw:flex-col tw:gap-2 tw:px-4"
    >
      {formFields.map(field => (
        <div key={field.name} className="tw:group tw:relative">
          <DynamicFieldRenderer
            field={field}
            control={control}
            fileUploadFn={fileUploadFn}
            error={errors[field.name]?.message as string}
            onFieldChange={updateField}
          />
          <button
            type="button"
            onClick={() => removeField(field.name)}
            aria-label={Localization.translator.remove}
            className="tw:absolute tw:right-1 tw:top-1/2 tw:flex tw:size-6 tw:-translate-y-1/2 tw:items-center tw:justify-center tw:rounded-md tw:border-none tw:bg-white tw:text-muted-foreground tw:opacity-0 tw:transition-opacity tw:cursor-pointer tw:group-hover:opacity-100 tw:hover:text-destructive"
          >
            <Trash2 className="tw:size-6" />
          </button>
        </div>
      ))}
    </form>
  )
}

const Footer: React.FC = () => {
  const {
    state: {
      isSaving,
      saveError,
      isConfirmOpen,
      pendingFieldCount,
      selectedCount,
    },
    actions: { confirmSave, cancelConfirm },
  } = useResourceBulkEdit()

  return (
    <div className="tw:flex tw:flex-col tw:p-6 tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-background">
      {saveError && (
        <div className="tw:text-body-sm tw:text-destructive tw:mb-2">
          {saveError}
        </div>
      )}
      <Button
        type="submit"
        size="lg"
        form={DYNAMIC_FORM_ID}
        disabled={isSaving}
        data-testid="resource-bulk-edit-submit"
      >
        {isSaving
          ? Localization.translator.savingEllipsis
          : Localization.translator.bulkUpdate}
      </Button>
      <ConfirmDialog
        isOpen={isConfirmOpen}
        onCancelClicked={cancelConfirm}
        onDoneClicked={confirmSave}
        doneTitle={Localization.translator.bulkUpdate}
        loading={isSaving}
        question={Localization.translator.bulkEditConfirmQuestion(
          pendingFieldCount,
          selectedCount,
        )}
      />
    </div>
  )
}

export const ResourceBulkEdit = {
  Root,
  Header,
  Content,
  FieldSelector,
  Form,
  Footer,
  useResourceBulkEdit,
}
